/**
 * Item-bound scanner history for one message (docs/plan/mail-scanner.md §14 E1): the recipient's simplified projection
 * of what the scanner recorded for this exact item. Same stage/outcome wording (`common.quarantineHistory.*`) and the
 * same panel as the admin quarantine history; `unavailable` is shown instead of any guess.
 */
import { useI18n, type MessageKey } from '@platform/i18n';
import { QuarantineHistoryPanel } from '../composites/quarantine-history';
import type { MessageAnalysisView } from '../mail/analysis';

export function ScannerHistorySection({ history }: { history: NonNullable<MessageAnalysisView['history']> }) {
  const { t, formatDate } = useI18n();
  const events = history.events.map((event, index) => ({
    id: `${event.ts}:${event.stage}:${index}`,
    title: t(`common.quarantineHistory.${event.stage}` as MessageKey),
    time: formatDate(event.ts, { dateStyle: 'medium', timeStyle: 'short' }),
    dateTime: event.ts,
    tone: event.outcome === 'success' ? ('ok' as const) : event.outcome === 'failure' ? ('error' as const) : ('unknown' as const),
    meta: t(`common.quarantineHistory.${event.outcome}` as MessageKey),
  }));
  return (
    <QuarantineHistoryPanel
      title={t('mailDetails.security.history')}
      partialNotice={t('common.quarantineHistory.partial')}
      unavailableNotice={t('common.quarantineHistory.unavailable')}
      emptyNotice={t('common.quarantineHistory.empty')}
      truncatedNotice={t('common.quarantineHistory.truncated')}
      timelineLabel={t('common.quarantineHistory.timeline')}
      loadingLabel={t('common.quarantineHistory.loading')}
      retryLabel={t('common.quarantineHistory.retry')}
      firstPageLabel={t('common.quarantineHistory.firstPage')}
      nextLabel={t('common.quarantineHistory.next')}
      status={history.status}
      events={events}
      showTruncatedNotice={history.truncated}
    />
  );
}
